import { useState, useRef, useEffect } from 'react';
import { 
    TitleLarge,
    TitleMedium,
    BodyXLMedium,
    BodyRegular
} from '@/components/typography';
import { PrimaryButton } from '@/components/buttons';
import { GenericCard } from '@/components/cards';
import { Accordion, TextBadge } from '@/components/elements';
// Doughnut chart deps:
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Ico structure images
import HeroImage from '@/assets/img/ui/26.png';
// import Placeholder from '@/assets/img/ui/8.png';
import IconStartingPrice from '@/assets/img/ui/18.png';
import IconHourly from '@/assets/img/ui/19.png';
import IconWallet from '@/assets/img/ui/20.png';
import IconCurrencies from '@/assets/img/ui/1.png';
import IconShare from '@/assets/img/ui/21.png';
import IconInputAmount from '@/assets/img/ui/2.png';
import IconConfirmTransaction from '@/assets/img/ui/16.png';
import IconTokens from '@/assets/img/ui/22.png';

export const ICO = () => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [isInViewport, setIsInViewport] = useState(false);
    const chartRef = useRef<HTMLDivElement>(null);
    const [chartKey, setChartKey] = useState(0); // Force chart re-render

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInViewport(true);
                        setChartKey(prev => prev + 1); // Force chart to re-mount
                        observer.disconnect();
                    }
                });
            },
            { threshold: 0.3 }
        );

        if (chartRef.current) {
            observer.observe(chartRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    ChartJS.register(ArcElement, Tooltip, Legend);

    return (
        <div className="w-full bg-background">
            {/* Hero Section */}
            <section className="w-full flex justify-center px-4 pb-8 pt-16 lg:pt-44 relative ">
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                    {/* Star patterns will go here */}
                </div>

                {/* Glow effects */}
                <div className="absolute right-1/4 bottom-1/4 w-96 h-96 opacity-60 bg-cyan-400 rounded-full blur-[282.13px] pointer-events-none" />

                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12 relative z-10">
                    {/* Text and all */}
                    <div className="w-full lg:flex-1 flex flex-col items-start gap-4 lg:gap-6 lg:max-w-[716px]">
                        <TextBadge>ICO</TextBadge>
                        <TitleLarge className="text-white">
                            Own a Piece of the Future – Join the <span className="text-primary">CCRAP ICO</span>
                        </TitleLarge>
                    </div>

                    {/* Image (hidden on mobile) */}
                    <div className="hidden lg:flex lg:flex-1 items-center justify-center relative">
                        <div className="absolute top-0 right-1/4 w-96 h-96 opacity-60 bg-primary rounded-full blur-[282.13px] pointer-events-none" />
                        <img 
                            className="absolute -top-60 right-0 w-[571.65px] h-[571.65px] rotate-[3.95deg]" 
                            src={HeroImage}
                            alt="ICO Hero section image"
                        />
                    </div>
                </div>
            </section>

            {/* ICO Structure Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-start lg:items-center gap-6 lg:gap-12">
                    <TitleMedium className="text-white mb-4 lg:mb-0">
                        ICO <span className="text-primary">Structure</span>
                    </TitleMedium>

                    <div className="w-full grid grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-12 lg:gap-5">
                        <GenericCard
                            image={IconStartingPrice}
                            title="Starting Price"
                            description="$0.002 per token"
                            titleClassName="max-w-none"
                        />
                        <GenericCard
                            image={IconHourly}
                            title="Hourly Increment"
                            description="+$0.0000417"
                            titleClassName="max-w-none"
                        />
                        <GenericCard
                            image={IconWallet}
                            title="Wallet Cap"
                            description="5,000,000 CCRAP"
                            titleClassName="max-w-none"
                        />
                        <GenericCard
                            image={IconCurrencies}
                            title="Accepted Currencies"
                            description="SOL & USDC"
                            titleClassName="max-w-none"
                        />
                        <div className="col-span-2 lg:col-span-1">
                            <GenericCard
                                image={IconShare}
                                title="ICO Share"
                                description="10% of total 1T supply"
                                titleClassName="max-w-none"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Distribution Section */}
            <section className="w-full flex justify-center px-4 py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-center gap-6 lg:gap-12">
                    <div className="flex flex-col items-center gap-4">
                        <TitleMedium className="text-white text-center">
                            <span className="text-primary">Distribution</span> of Proceeds
                        </TitleMedium>
                        <div className="flex items-center gap-2">
                            <BodyRegular className="text-white/60">Duration:</BodyRegular>
                            <BodyRegular className="text-white font-semibold">Until all 10% sold</BodyRegular>
                        </div>
                    </div>

                    <div className="w-full flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-14">
                        {/* Pie Chart (Chart.js, minimal interactive) */}
                        <div ref={chartRef} className="w-52 h-52 lg:w-72 lg:h-72 flex-shrink-0">
                            {isInViewport && (
                                <Doughnut
                                    key={chartKey}
                                    data={{
                                        datasets: [{
                                            data: [40, 59, 1],
                                            backgroundColor: [
                                                '#67E8F9',
                                                '#89FF69',
                                                '#FCD34D',
                                            ],
                                            borderWidth: 0,
                                            spacing: 8,
                                            hoverOffset: -15,
                                            hoverBorderWidth: 0
                                        }]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        cutout: '75%',
                                        rotation: 75,
                                        circumference: 360,
                                        animation: {
                                            animateRotate: true,
                                            animateScale: true,
                                            duration: 300,
                                            easing: 'easeInOutQuad',
                                        },
                                        onHover: (_event, activeElements) => {
                                            if (activeElements.length > 0) {
                                                setHoveredIndex(activeElements[0].index);
                                            } else {
                                                setHoveredIndex(null);
                                            }
                                        },
                                        plugins: {
                                            legend: {
                                                display: false
                                            },
                                            tooltip: {
                                                enabled: false
                                            }
                                        },
                                        elements: {
                                            arc: {
                                                borderWidth: 0,
                                                borderRadius: 8,
                                            }
                                        }
                                    }}
                                />
                            )}
                        </div>

                        {/* Legend & CTA, with hover effects */}
                        <div className="w-full lg:w-80 flex flex-col gap-4">
                            <div className="flex flex-col gap-4">
                                <div 
                                    className={`w-full p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex items-center gap-2 transition-all ease-in-out duration-300 ${
                                        hoveredIndex === 0 ? 'scale-105 brightness-125' : ''
                                    }`}
                                    onMouseEnter={() => setHoveredIndex(0)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                >
                                    <div className="w-4 h-4 bg-cyan-300 rounded-full flex-shrink-0" />
                                    <BodyRegular className="text-white font-semibold">40%</BodyRegular>
                                    <BodyRegular className="text-white/60">Liquidity Pools (locked + POL)</BodyRegular>
                                </div>
                                <div 
                                    className={`w-full p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex items-center gap-2 transition-all ease-in-out duration-300 ${
                                        hoveredIndex === 1 ? 'scale-105 brightness-125' : ''
                                    }`}
                                    onMouseEnter={() => setHoveredIndex(1)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                >
                                    <div className="w-4 h-4 bg-primary rounded-full flex-shrink-0" />
                                    <BodyRegular className="text-white font-semibold">59%</BodyRegular>
                                    <BodyRegular className="text-white/60">Treasury (Marketing, Dev, Ops)</BodyRegular>
                                </div>
                                <div 
                                    className={`w-full p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex items-center gap-2 transition-all ease-in-out duration-300 ${
                                        hoveredIndex === 2 ? 'scale-105 brightness-125' : ''
                                    }`}
                                    onMouseEnter={() => setHoveredIndex(2)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                >
                                    <div className="w-4 h-4 bg-amber-300 rounded-full flex-shrink-0" />
                                    <BodyRegular className="text-white font-semibold">1%</BodyRegular>
                                    <BodyRegular className="text-white/60">Team Compensation</BodyRegular>
                                </div>
                            </div>
                            <PrimaryButton className="w-full">Join the ICO</PrimaryButton>
                        </div>
                    </div>
                </div>
            </section>

            {/* Participation Steps Section */}
            <section className="w-full flex justify-center px-4 py-16 lg:py-24 relative overflow-hidden">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-center gap-6 lg:gap-16 relative z-10">
                    <TitleMedium className="text-white text-center">
                        <span className="text-primary">Participation</span> Steps
                    </TitleMedium>

                    {/* Steps container */}
                    <div className="w-full flex flex-col items-center gap-8 lg:gap-20 relative">
                        {/* Vector doodad - desktop only */}
                        <svg className='hidden lg:block absolute h-[120%] -top-12' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 900" fill="none">
                            <path d="M12.1531 1.50019C13.2063 12.3794 31.5496 229.717 21.5021 425.002C11.4546 620.287 -1.55001 748.365 2.14142 897.696" stroke="url(#paint0_linear_259_2223)" strokeWidth="3" strokeMiterlimit="10" strokeLinecap="round"/>
                            <defs>
                                <linearGradient id="paint0_linear_259_2223" x1="12.9998" y1="897.612" x2="6.1108" y2="1.54665" gradientUnits="userSpaceOnUse">
                                    <stop offset="0.271465" stopColor="#89FF69" stopOpacity="0"/>
                                    <stop offset="0.5" stopColor="#89FF69"/>
                                    <stop offset="0.816742" stopColor="#89FF69" stopOpacity="0"/>
                                </linearGradient>
                            </defs>
                        </svg>

                        <div className="flex items-center gap-4 w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:right-[11.5%]">
                            <img 
                                src={IconWallet} 
                                className="absolute w-28 lg:w-64 -left-16 lg:left-64 top-1/2 -translate-y-1/2 rotate-[-14.54deg] z-0" 
                                alt="Connect your Solana wallet background image" 
                            />
                            
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">1</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:w-64 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1">
                                <BodyRegular className="text-white lg:text-center lg:font-medium">Connect your Solana wallet</BodyRegular>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 flex-row-reverse w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:left-[11.5%]">
                            <img 
                                src={IconStartingPrice} 
                                className="absolute w-28 lg:w-72 -right-16 lg:right-52 top-[120%] -translate-y-1/2 rotate-[-30.06deg] z-0" 
                                alt="Review live token price background image" 
                            />
                            
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">2</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:w-64 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1">
                                <BodyRegular className="text-white lg:text-center lg:font-medium">Review live token price</BodyRegular>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:right-[11.5%]">
                            <img 
                                src={IconInputAmount} 
                                className="absolute w-28 lg:w-80 -left-16 lg:left-60 top-1/4 -translate-y-1/2 rotate-[-1.29deg] z-0" 
                                alt=">Enter the amount (up to cap) background image" 
                            />
                            
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">3</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:w-64 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1">
                                <BodyRegular className="text-white lg:text-center lg:font-medium">Enter the amount (up to cap)</BodyRegular>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 flex-row-reverse w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:left-[11.5%]">
                            <img 
                                src={IconConfirmTransaction} 
                                className="absolute w-28 lg:w-80 -right-16 lg:right-60 top-[120%] -translate-y-1/2 rotate-[-1.960deg] z-0" 
                                alt="Confirm transaction background image" 
                            />
                            
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">4</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:w-64 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1">
                                <BodyRegular className="text-white lg:text-center lg:font-medium">Confirm transaction</BodyRegular>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 lg:gap-4 w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:right-[11.5%]">
                            <img 
                                src={IconTokens} 
                                className="absolute w-28 lg:w-72 -left-16 lg:left-60 top-1/2 -translate-y-1/2 z-0" 
                                alt="Receive tokens directly to your wallet background image" 
                            />
                            
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">5</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:w-64 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1">
                                <BodyRegular className="text-white lg:text-center lg:font-medium">Receive tokens directly to your wallet</BodyRegular>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="w-full flex justify-center px-4 py-16">
                <div className="w-full max-w-[320px] lg:max-w-[800px] flex flex-col items-center gap-6 lg:gap-8">
                    <TitleMedium className="text-white">FAQ</TitleMedium>

                    <div className="w-full flex flex-col gap-2">
                        <Accordion
                            question="Can I participate more than once?"
                            answer="Yes, until reaching the wallet cap."
                            defaultOpen={true}
                            className="w-full"
                        />
                        <Accordion
                            question="Is there vesting on ICO tokens?"
                            answer="No, ICO tokens are immediately available in your wallet."
                            className="w-full"
                        />
                        <Accordion
                            question="Is there a refund?"
                            answer="All sales are final. Please review carefully before purchase."
                            className="w-full"
                        />
                        <Accordion
                            question="When will staking open?"
                            answer="Staking will be available immediately after ICO completion."
                            className="w-full"
                        />
                    </div>

                    <PrimaryButton className="w-64">Whitepaper</PrimaryButton>
                </div>
            </section>
        </div>
    );
};